import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import type { ResourceTypeDto } from '@/api/generated';

import { useApiClient } from '@/api/clients/useApiClient.util.ts';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';

type Req = {
  file: File;
  resourceId: string;
  resourceType: ResourceTypeDto;
};

type Return = {
  isUploading: boolean;
  uploadFile: (req: Req) => Promise<void>;
};

export const useUploadFile = (): Return => {
  const { attachmentsApi } = useApiClient();
  const queryClient = useQueryClient();
  const { userId } = useUserContext();
  const { isPending, mutateAsync } = useMutation<void, AxiosError, Req>({
    mutationFn: async req => {
      const { data } = await attachmentsApi.addAttachmentFile(
        userId,
        req.resourceId,
        req.resourceType,
        req.file,
      );
      return data;
    },
    onSuccess: async (_data, req) => {
      await queryClient.refetchQueries({
        queryKey: [QueryKeyEnum.FILES, req.resourceId, req.resourceType],
      });
    },
  });

  return {
    isUploading: isPending,
    uploadFile: mutateAsync,
  };
};
